import React from "react"
import { ShoppingCartIcon, TruckIcon, PlayCircleIcon } from "@heroicons/react/24/outline"
import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"
import Img from "./Img"
import Button from "aldoo-ra/Button"
import PriceDisplay from "./PriceDisplay"
import ProductRating from "./ProductRating"
import useT from "../hooks/useT"
import { analyzeRatings } from "../utils/ratings"
import { buildContentUrl } from "../utils/contentRoutes"
import { presaleOf } from "../utils/presale"
import { basePrice, isPhysicalProduct, isSoldInCoins, isOwned, isOutOfStock, isPlayerContent, isRepeatPurchase } from "../utils/productUtils"
import { openPlayer } from "../player"
import { navigate } from "vike/client/router"
import { useCurrency } from "../store/currency"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { addToCart, useCart, cartLineOf } from "../store/cart"
import { needsSelection } from "aldoo-ra/Store/extensions"
import { useStoreAccount, ownsProduct } from "../store/account"
import CartStepper from "./cart/CartStepper"
import { usePlacement } from "../store/placements"
import { listOf, trackListImpression, trackSelectItem } from "../tracking"
import FavoriteButton from "./FavoriteButton"

const DISCOUNT_CHIP = "inline-flex shrink-0 h-[24px] items-center rounded  bg-primary_hov text-sm font-condensed font-medium text-white px-2"

// Price block shared by cards and rows; a card (align center) puts the
// discount chip after the prices, a row (align end) under the struck original
export function ProductPrice({ product, align = "start", size = "small", showDiscount = true }) {
  const t = useT()
  const { currency, convert } = useCurrency()
  // rows (medium) keep the old green "Free" emphasis; cards stay muted
  if (product.free_access) return <span className={size === "medium" ? "text-[18px] font-semibold text-success" : "text-[13px] text-text_secondary"}>{t`Free`}</span>

  const discountPercent = activeDiscountPercent(product)
  // the product is shown at the price it is sold at (its primary model)
  const showCoins = isSoldInCoins(product)

  if (showCoins && (product.cost || product.rent_cost)) {
    return <PriceDisplay amount={product.rent_cost ?? product.cost} currency="coins" divisor={1} variant={size} />
  }

  // the product's price in the currency the visitor chose
  const base = basePrice(product)
  if (!base) return null
  const parsed = { amount: convert(base.amount, base.currency), currency }

  const discounted = applyDiscount(parsed.amount, discountPercent)

  // a row (align end), as the product page's sticky bar: the price paid, and
  // beside it the struck original over the discount chip
  if (align === "end") {
    return (
      <span className="inline-flex flex-row items-center gap-2">
        <PriceDisplay
          amount={discounted}
          currency={parsed.currency}
          variant={size}
          className={twMerge("min-w-0", discountPercent > 0 && "text-primary")}
        />
        {discountPercent > 0 && (
          // top-aligned with the price's cents (their pt-[2px]), at their size
          <span className="inline-flex flex-col items-stretch self-start pt-[2px]">
            <PriceDisplay amount={parsed.amount} currency={parsed.currency} variant={`${size}_struck`} struck className="opacity-60" />
            {showDiscount && <span className={twMerge(DISCOUNT_CHIP, "h-[16px] justify-center")}>-{discountPercent}%</span>}
          </span>
        )}
      </span>
    )
  }

  return (
    // a card (align center) centres the prices, the chip last
    <span className={twMerge("flex flex-1 min-w-0 items-center justify-between gap-1", align === "center" && "justify-center")}>
      {/* the price paid first, the struck original after it; on a narrow card the struck one wraps under */}
      <span className={twMerge("inline-flex flex-wrap items-center gap-x-1 min-w-0", align === "center" && "justify-center")}>
        <PriceDisplay
          amount={discounted}
          currency={parsed.currency}
          variant={size}
          // a card's discounted price stays black; the struck original carries the discount
          className={twMerge("min-w-0", discountPercent > 0 && "text-text")}
        />
        {discountPercent > 0 && (
          // top-aligned with the price's cents (their pt-[2px]), at their size
          <PriceDisplay amount={parsed.amount} currency={parsed.currency} variant="small_struck" struck className="opacity-50 self-start pt-[2px]" />
        )}
        {align === "center" && showDiscount && discountPercent > 0 && <span className={DISCOUNT_CHIP}>-{discountPercent}%</span>}
      </span>
      {align !== "center" && showDiscount && discountPercent > 0 && (
        <span className={DISCOUNT_CHIP}>-{discountPercent}%</span>
      )}
    </span>
  )
}

// the product's author line: its display name, or its authors' names; "" when none
export function authorNames(product) {
  if (product.author_display_name) return product.author_display_name
  if (!Array.isArray(product.author)) return ""
  return product.author.filter((a) => a?.name).map((a) => a.name).join(", ")
}

// goods carry a brand instead of an author: the product's own brand, or its
// merchant's name standing in for it (a clothing merchant sells its own make)
export function brandName(product) {
  return product.brand || product.publisher || ""
}

// what the card names under the title: the authors, or the brand when there are none
function AuthorNames({ product, className = "" }) {
  const names = authorNames(product) || brandName(product)
  if (!names) return null
  return <span className={className}>{names}</span>
}

/**
 * Product card: square cover, title, author, price, rating, hover add-to-cart
 * for physical real-money products. variant: default | square_sm
 */
// priority: a card of a list's first row — its cover is the largest thing on screen, so it loads at once and first
export default function ProductBox({ product, variant = "default", styleOverrides, hideProductDetails = false, className = "", placement: ownPlacement, priority = false }) {
  // where its add is counted from: the list it is in (store/placements), unless said here
  const placement = usePlacement(ownPlacement)
  const t = useT()
  const v = VariantReader.useVariant({ componentName: "ProductBox", variant, styleOverrides })
  const { account } = useStoreAccount()
  // a card of a list on screen, and opened from it (GA4 view_item_list / select_item)
  const list = listOf(placement)
  React.useEffect(() => {
    if (product?.pid && list) trackListImpression(product, list)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.pid, list?.id])
  if (!product?.pid) return null

  const { avgRating, numRatings } = analyzeRatings(product.ratings)
  const url = buildContentUrl(product)
  // anything sold for money that the reader does not own yet — a digital
  // album as much as a book; what is owned (the player's content: a licence
  // or a paper book is bought again) gets "Open" in the pill's place
  // a licence or a parcel is bought again, never opened
  const owned = !isRepeatPurchase(product) && (isOwned(product) || ownsProduct(account, product.pid))
  const canAddToCart =
    !product.free_access && (product.real_money_cost || product.real_price) && !isSoldInCoins(product) && !owned

  const handleOpen = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isPlayerContent(product)) return openPlayer(product.pid)
    // a download: the account's digital products, on that one product
    navigate(`/user/digital-products?q=${encodeURIComponent(product.pid)}`)
  }

  const handleAddToCart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isOutOfStock(product)) return
    // a product the buyer has to choose for (a size, an outfit's parts) is
    // chosen on its page
    if (needsSelection(product)) return navigate(url)
    // the cart says "Added to cart" itself (store/cart.js)
    addToCart(product, 1, {}, { placement })
  }
  // in the cart already: the card shows the stepper instead of the hover button
  // (the product's plain line; the lines of a selection are stepped in the cart)
  const cart = useCart()
  const inCart = cartLineOf(cart, product.pid)?.quantity || 0
  // sold out or not on sale: the pill says so and adds nothing (a product with
  // sizes or parts answers by its own stock state, the list's derived in_stock)
  const outOfStock = isOutOfStock(product)
  // the row under the picture: Open, or the cart pill / stepper
  const hasAction = owned || canAddToCart

  return (
    <div className={twMerge(v.container, className)}>
      <Button 
        variant="none" 
        href={url} 
        aria-label={product.name}
        onClick={() => list && trackSelectItem(product, list)}
      >
        <div className={v.cover}>
          {/* every card fits the picture: the original in its own aspect, the square when there is none */}
          <Img src={product.original || product.thumb || product.square} alt={product.name} width={232} height={232} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : undefined} className={twMerge("w-full h-full", product.second_image && v.imageFront)} imgClassName={v.image} />
          {/* a product with more pictures turns to its second one under the pointer (the back of a shirt) */}
          {product.second_image && (
            <Img src={product.second_image} alt="" width={232} height={232} className={v.imageBack} imgClassName={v.image} />
          )}

          {/* the pre-sale and free-shipping chips in the picture's top right corner (the discount sits by the price) */}
          <div className={v.badges}>
            {presaleOf(product) && (
              <span className={v.presaleBadge} data-testid="presale-badge">
                {t`Pre-sale`}
              </span>
            )}
            {product.free_shipping && isPhysicalProduct(product) && (
              <span className={v.badge}>
                <TruckIcon className="w-3 h-3" />
                {t`Free Shipping`}
              </span>
            )}
          </div>

          {/* the small square card names the product over its picture on hover; the full card's picture is left alone */}
          {variant === "square_sm" && (
            <div className={v.overlay}>
              <span>{product.name}</span>
            </div>
          )}

          {/* Old ProductBox: expired rentals in the library carry a "Renew" band over the cover */}
          {product.expired && !product.free_access && (
            <div className="absolute inset-x-0 bottom-0 h-[150px] rounded-lg bg-gradient-to-t from-black to-transparent" data-testid="renew-overlay">
              <span className="absolute inset-x-0 bottom-4 flex h-6 items-center justify-center bg-primary text-[14px] font-medium text-white">{t`Renew`}</span>
            </div>
          )}
        </div>
      </Button>

      {!hideProductDetails && variant !== "square_sm" && (
        <div className={v.details}>
          {/* right under the picture, in the same spot on every card: the
              add-to-cart pill, and once it is in the cart a stepper of the
              same shape — a shortcut for more of it (or none); the heart
              for the account's Favorites beside it */}
          {hasAction && (
            <div className={v.actionRow}>
              {owned && (
                <button type="button" className={v.cart} onClick={handleOpen} aria-label={t`Open`} data-testid="card-open">
                  <PlayCircleIcon className="w-5 h-5" />
                  <span>{t`Open`}</span>
                </button>
              )}
              {canAddToCart && inCart > 0 && <CartStepper product={product} className={v.stepper} placement={placement} />}
              {canAddToCart && inCart === 0 && outOfStock && (
                <button type="button" className={twMerge(v.cart, "opacity-50 cursor-not-allowed")} disabled aria-label={t`Out of Stock`} data-testid="card-out-of-stock">
                  <span>{t`Out of Stock`}</span>
                </button>
              )}
              {canAddToCart && inCart === 0 && !outOfStock && (
                <button 
                  type="button" 
                  className={v.cart} 
                  onClick={handleAddToCart} 
                  aria-label={t`Add to Cart`} 
                  data-testid="card-add-to-cart">
                  <ShoppingCartIcon className="w-5 h-5" 
                />
                  {/* the card's short form: "Добави" in Bulgarian, where the full string is too long for the tile */}
                  <span>{t`Add to cart`}</span>
                </button>
              )}
              <FavoriteButton product={product} className={v.favorite} fillOnHover />
            </div>
          )}
          <Button 
            variant="none" 
            href={url} 
            className={twMerge(v.title, "!leading-tight")}
            title={product.name}
          >
            {product.name}
          </Button>
          {/* the author's line is kept even when there is none, so every card's
              price sits at the same height */}
          <div className={v.authorRow}>
            <AuthorNames product={product} className={v.author} />
          </div>
          <div className={v.priceRow}>
            <ProductPrice product={product} align="center" />
            {/* no pill to sit beside: the heart at the right end of the prices' line */}
            {!hasAction && <FavoriteButton product={product} className="text-text shrink-0" />}
          </div>
          {/* the rating on a line of its own: the stars never fight the price for the width */}
          {numRatings > 0 && (
            <div className={v.ratingRow}>
              <ProductRating avgRating={avgRating} numRatings={numRatings} hideRatingNumber />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
